import type { MouseEventHandler } from 'react';
import { forwardRef, useCallback, useContext } from 'react';

import AtlasKitEditIcon from '@atlaskit/icon/core/edit';
import { useFeatureGate } from '@trello/feature-gate-client';
import { intl } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { EditIcon } from '@trello/nachos/icons/edit';
import { Tooltip } from '@trello/nachos/tooltip';
import type { CardFrontTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { CardFrontContext } from './CardFrontContext';

import styles from './CardFrontEditButton.less';

export const CardFrontEditButton = forwardRef<HTMLButtonElement>((_, ref) => {
  const { openQuickCardEditorOverlay } = useContext(CardFrontContext);

  const onClick = useCallback<MouseEventHandler>(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      openQuickCardEditorOverlay();
    },
    [openQuickCardEditorOverlay],
  );

  const { value: isNewCardDoneStateFeatureEnabled } = useFeatureGate(
    'phx_new_card_done_state_workspace',
  );

  return (
    <Tooltip
      content={intl.formatMessage({
        id: 'templates.card_front.edit-card',
        defaultMessage: 'Edit card',
        description: 'Edit card tooltip',
      })}
    >
      <Button
        aria-label={intl.formatMessage({
          id: 'templates.card_front.edit-card',
          defaultMessage: 'Edit card',
          description: 'Edit card button',
        })}
        className={styles.editButton}
        iconBefore={
          isNewCardDoneStateFeatureEnabled ? (
            <AtlasKitEditIcon
              label={intl.formatMessage({
                id: 'templates.card_front.edit-card',
                defaultMessage: 'Edit card',
                description: 'Edit card button',
              })}
              color="currentColor"
            />
          ) : (
            <EditIcon size="small" />
          )
        }
        onClick={onClick}
        ref={ref}
        tabIndex={-1}
        testId={getTestId<CardFrontTestIds>('quick-card-editor-button')}
      />
    </Tooltip>
  );
});
